import { useDeleteManufacturerMutation, useGetManufacturerQuery } from '@codezo/graphql';
import { formatDate, formatMonthYear } from '../../lib/utils';
import { ArrowLeft, ChevronRight, ExternalLink, Factory, Globe, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';
import { useSetPageMeta } from '../../hooks/useSetPageMeta';
import { EditManufacturerModal } from './EditManufacturerModal';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { SectionCard } from '../../components/ui/section-card';
import { EmptyState } from '../../components/ui/empty-state';
import { DetailRow } from '../../components/ui/detail-row';
import { StatusBadge } from '../../components/ui/status-badge';
import { InitialsAvatar } from '../../components/ui/initials-avatar';

/* ── Loading skeleton ── */

function DetailSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			<div className="mb-5 h-4 w-24 animate-pulse rounded bg-surface-fill" />
			<div className="mb-7 flex items-start gap-4">
				<div className="h-[72px] w-[72px] animate-pulse rounded-[var(--r-3)] bg-surface-fill" />
				<div className="flex-1">
					<div className="mb-2 h-7 w-48 animate-pulse rounded bg-surface-fill" />
					<div className="h-4 w-72 animate-pulse rounded bg-surface-fill" />
				</div>
			</div>
			<div className="mb-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[var(--r-3)] border border-line-1 bg-surface-sunken px-5 py-4"
					>
						<div className="mb-2 h-3 w-16 animate-pulse rounded bg-surface-fill" />
						<div className="h-6 w-10 animate-pulse rounded bg-surface-fill" />
					</div>
				))}
			</div>
		</div>
	);
}

/* ════════════════════════ MANUFACTURER DETAIL PAGE ════════════════════════ */

export function ManufacturerDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const [editOpen, setEditOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);

	const { data, loading, error, refetch } = useGetManufacturerQuery({
		variables: { id: id! },
		fetchPolicy: 'no-cache',
		skip: !id,
	});

	const m = data?.getManufacturer;
	useSetPageMeta(m?.name || 'Manufacturer', [
		{ label: 'Manufacturers', path: '/suppliers' },
		{ label: m?.name || 'Loading...' },
	]);

	const [deleteManufacturer, { loading: deleting }] = useDeleteManufacturerMutation();
	const handleDelete = async () => {
		if (!m) return;
		try {
			await deleteManufacturer({ variables: { input: { id: m.id } } });
			toast.success('Manufacturer deleted');
			navigate('/suppliers');
		} catch (err) {
			toast.error(err instanceof Error ? err.message : 'Failed to delete manufacturer');
		}
	};

	if (loading) {
		return <DetailSkeleton />;
	}

	// getManufacturer is non-nullable, so a missing id resolves as a GraphQL error.
	if (error || !m) {
		return (
			<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
				<button
					onClick={() => navigate('/suppliers')}
					className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
				>
					<ArrowLeft size={14} />
					Manufacturers
				</button>
				<EmptyState
					icon={Factory}
					title="Manufacturer not found"
					description="This manufacturer may have been deleted or the link is invalid."
				>
					<Button
						variant="outline"
						size="sm"
						className="mt-1"
						onClick={() => navigate('/suppliers')}
					>
						Back to manufacturers
					</Button>
				</EmptyState>
			</div>
		);
	}

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8">
			{/* ── Back ── */}
			<button
				onClick={() => navigate('/suppliers')}
				className="mb-4 flex items-center gap-1.5 bg-transparent text-sm text-text-3 transition-colors hover:text-text-1"
			>
				<ArrowLeft size={14} />
				Manufacturers
			</button>

			{/* ── Header ── */}
			<div className="mb-7 flex items-start gap-4">
				<InitialsAvatar name={m.name} src={m.logoUrl} size={72} shape="rounded" />
				<div className="min-w-0 flex-1">
					<div className="mb-1 flex flex-wrap items-center gap-2.5">
						<h1 className="text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
							{m.name}
						</h1>
						<StatusBadge active={m.isActive} />
					</div>
					<div className="flex flex-wrap items-center gap-3.5 text-base text-text-3">
						{m.country && (
							<span className="flex items-center gap-1.5">
								<MapPin size={12} />
								{m.country}
							</span>
						)}
						{m.website && (
							<span className="flex items-center gap-1.5">
								<Globe size={12} />
								{m.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
							</span>
						)}
						{m.createdAt && (
							<>
								<span className="text-text-4">·</span>
								<span>Since {formatMonthYear(m.createdAt)}</span>
							</>
						)}
					</div>
				</div>
				<div className="flex shrink-0 flex-wrap gap-2">
					<Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
						Edit
					</Button>
					<Button variant="destructive" size="sm" onClick={() => setDeleteOpen(true)}>
						Delete
					</Button>
				</div>
			</div>

			{/* ── Metric strip ── */}
			<div className="mb-7 grid grid-cols-2 divide-x divide-y divide-line-1 overflow-hidden rounded-[var(--r-3)] border border-line-1 bg-surface-sunken sm:grid-cols-4">
				{[
					{ label: 'Status', value: m.isActive ? 'Active' : 'Inactive' },
					{ label: 'Country', value: m.country || '—' },
					{ label: 'Vetter', value: m.vetterEntity?.name || '—' },
					{
						label: 'Created',
						value: m.createdAt ? formatDate(m.createdAt) : '—',
					},
				].map((metric) => (
					<div key={metric.label} className="px-5 py-4">
						<div className="mb-1.5 text-2xs font-medium uppercase tracking-[0.08em] text-text-4">
							{metric.label}
						</div>
						<div className="font-mono text-lg font-medium text-text-1">
							{metric.value}
						</div>
					</div>
				))}
			</div>

			{/* ── Content ── */}
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				{/* Left column */}
				<div className="flex flex-col gap-4">
					{/* Description */}
					<SectionCard title="About">
						<div className="px-4 py-4 text-base leading-relaxed text-text-2">
							{m.description || 'No description provided.'}
						</div>
					</SectionCard>
				</div>

				{/* Right column */}
				<div className="flex flex-col gap-4">
					{/* Vetter assignment */}
					<SectionCard title="Assigned vetter">
						{m.vetterEntity ? (
							<>
								<div
									className="flex cursor-pointer items-center gap-3 px-4 py-3.5 transition-colors duration-[180ms] hover:bg-surface-hover"
									onClick={() => navigate(`/vetters/${m.vetterEntity!.id}`)}
								>
									<InitialsAvatar
										name={m.vetterEntity.name}
										size={36}
										shape="rounded"
									/>
									<div className="min-w-0 flex-1">
										<div className="text-base font-medium text-text-1">
											{m.vetterEntity.name}
										</div>
										<div className="text-xs text-text-4">
											{m.vetterEntity.isActive ? 'Active' : 'Inactive'}
										</div>
									</div>
									<ChevronRight size={14} className="text-text-4" />
								</div>
								{m.vetterEntity.primaryContact && (
									<div style={{ borderTop: '0.5px solid var(--line-1)' }}>
										<DetailRow
											label="Contact"
											value={`${m.vetterEntity.primaryContact.firstName} ${m.vetterEntity.primaryContact.lastName}`}
										/>
										{/* Both rows always render — a missing one reads as
										    "not on file" rather than silently collapsing. */}
										<DetailRow
											label="Email"
											value={
												m.vetterEntity.primaryContact.authenticationUser
													?.email ? (
													<a
														href={`mailto:${m.vetterEntity.primaryContact.authenticationUser.email}`}
														className="transition-colors hover:text-accent"
													>
														{
															m.vetterEntity.primaryContact
																.authenticationUser.email
														}
													</a>
												) : null
											}
										/>
										<DetailRow
											label="Phone"
											value={
												m.vetterEntity.primaryContact.phone ? (
													<a
														href={`tel:${m.vetterEntity.primaryContact.phone.replace(/\s+/g, '')}`}
														className="transition-colors hover:text-accent"
													>
														{m.vetterEntity.primaryContact.phone}
													</a>
												) : null
											}
											last
										/>
									</div>
								)}
							</>
						) : (
							<div className="px-4 py-4 text-sm text-text-4">
								No vetter assigned to this manufacturer.
							</div>
						)}
					</SectionCard>

					{/* Details card */}
					<SectionCard title="Details">
						{m.website && (
							<DetailRow
								label="Website"
								value={
									<a
										href={
											m.website.startsWith('http')
												? m.website
												: `https://${m.website}`
										}
										target="_blank"
										rel="noopener noreferrer"
										className="flex items-center gap-1 transition-colors hover:text-accent"
									>
										{m.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
										<ExternalLink size={11} />
									</a>
								}
							/>
						)}
						<DetailRow label="Country" value={m.country} />
						<DetailRow
							label="Created"
							value={m.createdAt ? formatDate(m.createdAt) : null}
						/>
						<DetailRow
							label="Updated"
							value={m.updatedAt ? formatDate(m.updatedAt) : null}
							last
						/>
					</SectionCard>
				</div>
			</div>

			<ConfirmDialog
				open={deleteOpen}
				onOpenChange={setDeleteOpen}
				title="Delete manufacturer"
				description={`Are you sure you want to delete "${m.name}"? Quotes or offers that reference this manufacturer may be affected. This action cannot be undone.`}
				onConfirm={handleDelete}
				loading={deleting}
			/>

			<EditManufacturerModal
				open={editOpen}
				onOpenChange={setEditOpen}
				manufacturer={
					m
						? {
								id: m.id,
								name: m.name,
								country: m.country,
								description: m.description,
								website: m.website,
								isActive: m.isActive,
								vetterEntityId: m.vetterEntity?.id,
							}
						: null
				}
				onSuccess={() => refetch()}
			/>
		</div>
	);
}
